import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Brain, Target, Users, Zap, Globe, Heart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useParallax } from '@/hooks/useParallax';
import DocumentTitle from '@/components/DocumentTitle';

const About = () => {
  const { user } = useAuth();
  
  // Refs for parallax elements
  const starsRef = useRef<HTMLDivElement>(null);
  const planet1Ref = useRef<HTMLDivElement>(null);
  const planet2Ref = useRef<HTMLDivElement>(null);
  
  // Parallax styles
  const starsStyle = useParallax(starsRef, { speed: 0.05 });
  const planet1Style = useParallax(planet1Ref, { speed: -0.1 });
  const planet2Style = useParallax(planet2Ref, { speed: -0.15 });

  const values = [
    {
      icon: <Brain className="h-8 w-8" />,
      title: "Innovation First",
      description: "We push the boundaries of AI technology to solve real-world video challenges."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "User-Centric",
      description: "Every feature we build starts with understanding our users' needs and workflows."
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Global Impact",
      description: "Democratizing access to advanced video intelligence for creators worldwide."
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Ethical AI",
      description: "Committed to responsible AI development that respects privacy and promotes fairness."
    }
  ];

  const stats = [
    { number: "1M+", label: "Videos Processed" },
    { number: "50K+", label: "Active Users" },
    { number: "99.9%", label: "Uptime" },
    { number: "150+", label: "Countries" }
  ];

  const team = [
    {
      name: "Sunny Shah",
      role: "Founder & CEO",
      bio: "Passionate about democratizing AI technology and making advanced video intelligence accessible to creators worldwide.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
      website: "https://www.sunnyshah.dev/"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-900 to-purple-900 text-white relative overflow-hidden">
      <DocumentTitle 
        title="About Us - VideoBrain.ai | Democratizing Video Intelligence"
        description="Learn about VideoBrain.ai's mission to democratize video intelligence. Discover our story, values, and commitment to making advanced AI tools accessible to creators worldwide."
      />
      
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md bg-black/20 sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-pink-600 flex items-center justify-center relative">
              <Brain className="h-6 w-6 text-white" />
              <div className="absolute inset-0 rounded-full bg-pink-600 animate-pulse opacity-50"></div>
            </div>
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-400">VideoBrain</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/features" className="text-gray-300 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</Link>
            <Link to="/roadmap" className="text-gray-300 hover:text-white transition-colors">Roadmap</Link>
            <Link to="/about" className="text-white font-semibold">About</Link>
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/login">Get Started</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Parallax background elements */}
      <div ref={starsRef} className="absolute inset-0 pointer-events-none" style={starsStyle}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgLz48L3N2Zz4=')]"></div>
        
        {/* Stars */}
        <div className="stars absolute inset-0">
          {[...Array(80)].map((_, i) => (
            <div 
              key={i} 
              className="absolute rounded-full bg-white" 
              style={{
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.7 + 0.3,
                animation: `twinkle ${Math.random() * 5 + 3}s infinite ${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Floating planets */}
      <div 
        ref={planet1Ref}
        className="absolute -right-32 top-1/4 w-64 h-64 rounded-full bg-gradient-to-br from-purple-300/20 to-indigo-600/20 blur-xl"
        style={planet1Style}
      ></div>
      
      <div 
        ref={planet2Ref}
        className="absolute -left-20 top-3/4 w-40 h-40 rounded-full bg-gradient-to-br from-pink-500/20 to-red-500/10 blur-lg"
        style={planet2Style}
      ></div>

      {/* Hero section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-300">
              Democratizing Video Intelligence
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
              We believe every creator should have access to powerful AI tools that unlock the hidden value in their video content.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-4xl font-bold">Our Mission</h2>
              </div>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                Founded in 2025, VideoBrain was born from a simple observation: video content was exploding, 
                but the tools to understand and navigate it hadn't kept pace. Hours of valuable content 
                remained locked away, unsearchable and difficult to repurpose.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Our mission is to democratize access to advanced video intelligence, making it as easy to 
                search through video content as it is to search the web. We're building the future where 
                every video becomes a searchable, interactive, and infinitely more valuable digital asset.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-black/30 backdrop-blur-sm relative z-10">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-400">
              Impact by Numbers
            </span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-extrabold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-400">
                  {stat.number}
                </div>
                <div className="text-gray-300 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-400">
              Our Values
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-pink-500/50 transition-all duration-300">
                <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center mb-6 text-white">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{value.title}</h3>
                <p className="text-gray-300 text-lg">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-black/30 backdrop-blur-sm relative z-10">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-400">
              Meet the Team
            </span>
          </h2>
          <div className="max-w-2xl mx-auto">
            {team.map((member, index) => (
              <a 
                key={index} 
                href={member.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center hover:border-pink-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg cursor-pointer"
              >
                <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden border-4 border-pink-500/20">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold mb-2">{member.name}</h3>
                <p className="text-pink-400 font-medium mb-4">{member.role}</p>
                <p className="text-gray-300 text-lg">{member.bio}</p>
                <div className="mt-4 text-pink-400 text-sm">
                  Visit Website →
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Join Our Journey</h2>
            <p className="text-xl text-gray-300 mb-8">
              Be part of the revolution in video intelligence. Start transforming your content today.
            </p>
            
            <Button size="lg" asChild className="bg-pink-600 hover:bg-pink-700 px-8 py-6 text-lg group transition-all duration-300">
              {user ? 
                <Link to="/dashboard" className="flex items-center">
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link> : 
                <Link to="/login" className="flex items-center">
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              }
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/40 backdrop-blur-md py-12 border-t border-white/10">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-6 md:mb-0">
              <div className="rounded-md bg-pink-600 p-1">
                <div className="h-6 w-6 text-white flex items-center justify-center font-bold">VB</div>
              </div>
              <span className="font-semibold text-xl">VideoBrain.ai</span>
            </div>
            <div className="flex gap-6">
              <Link to="/features" className="text-gray-300 hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</Link>
              <Link to="/roadmap" className="text-gray-300 hover:text-white transition-colors">Roadmap</Link>
              <Link to="/about" className="text-white font-semibold">About</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>
      {`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}
      </style>
    </div>
  );
};

export default About;
